import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ReviewSection from '@/components/ReviewSection';
import ShareModal from '@/components/ShareModal';
import ProductLightbox from '@/components/ProductLightbox';
import { ShoppingCart, Heart, Share2, Package, Shield, TrendingUp, Minus, Plus, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Play, ZoomIn } from 'lucide-react';
import { toast } from 'sonner';
import { useCartWishlist } from '@/context/CartWishlistContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Magnifier Component for Hover Zoom
const ImageMagnifier = ({ src, alt, magnifierHeight = 200, magnifierWidth = 200, zoomLevel = 2.5 }) => {
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [[x, y], setXY] = useState([0, 0]);
  const [[imgWidth, imgHeight], setSize] = useState([0, 0]);
  const imgRef = useRef(null);

  const mouseEnter = (e) => {
    const elem = e.currentTarget;
    const { width, height } = elem.getBoundingClientRect();
    setSize([width, height]);
    setShowMagnifier(true);
  };

  const mouseMove = (e) => {
    const elem = e.currentTarget;
    const { top, left } = elem.getBoundingClientRect();
    const x = e.pageX - left - window.pageXOffset;
    const y = e.pageY - top - window.pageYOffset;
    setXY([x, y]);
  };

  const mouseLeave = () => {
    setShowMagnifier(false);
  };

  return (
    <div className="relative inline-block w-full h-full">
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        onMouseEnter={mouseEnter}
        onMouseMove={mouseMove}
        onMouseLeave={mouseLeave}
      />

      {showMagnifier && (
        <div
          style={{
            position: 'absolute',
            pointerEvents: 'none',
            height: `${magnifierHeight}px`,
            width: `${magnifierWidth}px`,
            top: `${y - magnifierHeight / 2}px`,
            left: `${x - magnifierWidth / 2}px`,
            opacity: '1',
            border: '2px solid #dc2626',
            backgroundColor: 'white',
            backgroundImage: `url('${src}')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: `${imgWidth * zoomLevel}px ${imgHeight * zoomLevel}px`,
            backgroundPositionX: `${-x * zoomLevel + magnifierWidth / 2}px`,
            backgroundPositionY: `${-y * zoomLevel + magnifierHeight / 2}px`,
            borderRadius: '50%',
            boxShadow: '0 0 20px rgba(220, 38, 38, 0.5)',
            zIndex: 50,
          }}
        />
      )}
    </div>
  );
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [inWishlist, setInWishlist] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [productMedia, setProductMedia] = useState([]);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [thumbnailScrollPosition, setThumbnailScrollPosition] = useState(0);
  const thumbnailContainerRef = useRef(null);
  const { updateCartCount, updateWishlistCount } = useCartWishlist();

  const [showFullDescription, setShowFullDescription] = useState(false);


  const THUMBNAILS_VISIBLE_DESKTOP = 5;
  const THUMBNAILS_VISIBLE_MOBILE = 4;
  const THUMBNAIL_HEIGHT = 100;

  useEffect(() => {
    fetchProduct();
    fetchProductMedia();
    checkWishlistStatus();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Product not found');
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductMedia = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}/media`);
      if (response.data.success && response.data.media.length > 0) {
        setProductMedia(response.data.media);
      }
    } catch (error) {
      console.error('Failed to fetch product media:', error);
    }
  };

  const checkWishlistStatus = async () => {
    try {
      const response = await axios.get(`${API}/wishlist/check/${id}`, { withCredentials: true });
      setInWishlist(response.data.in_wishlist);
    } catch (error) {
      setInWishlist(false);
    }
  };

  const lightboxSlides = () => {
    const allMedia = productMedia.length > 0 ? productMedia : [{ file_url: product?.image_url, file_type: 'image' }];
    
    return allMedia.map(media => {
      if (media.file_type === 'video') {
        return {
          type: 'video',
          sources: [{ src: media.file_url, type: 'video/mp4' }],
        };
      }
      return { src: media.file_url };
    });
  };

  const scrollThumbnailsLeft = () => {
    if (thumbnailScrollPosition > 0) {
      setThumbnailScrollPosition(thumbnailScrollPosition - 1);
    }
  };

  const scrollThumbnailsRight = () => {
    const maxScroll = Math.max(0, productMedia.length - THUMBNAILS_VISIBLE_MOBILE);
    if (thumbnailScrollPosition < maxScroll) {
      setThumbnailScrollPosition(thumbnailScrollPosition + 1);
    }
  };

  const scrollThumbnailsUp = () => {
    if (thumbnailScrollPosition > 0) {
      setThumbnailScrollPosition(thumbnailScrollPosition - 1);
    }
  };

  const scrollThumbnailsDown = () => {
    const maxScroll = Math.max(0, productMedia.length - THUMBNAILS_VISIBLE_DESKTOP);
    if (thumbnailScrollPosition < maxScroll) {
      setThumbnailScrollPosition(thumbnailScrollPosition + 1);
    }
  };

  const handleDecreaseQuantity = () => {
    if (quantity <= 1) return;
    setQuantity(quantity - 1);
  };

  const handleIncreaseQuantity = () => {
    if (quantity >= 100) return;
    setQuantity(quantity + 1);
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setQuantity('');
      return;
    }
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return;
    if (numValue < 1) {
      setQuantity(1);
    } else if (numValue > 100) {
      setQuantity(100);
    } else {
      setQuantity(numValue);
    }
  };

  const handleQuantityBlur = () => {
    if (quantity === '' || quantity < 1) {
      setQuantity(1);
    } else if (quantity > 100) {
      setQuantity(100);
    }
  };

  const handleAddToCart = async () => {
    const finalQuantity = quantity === '' ? 1 : quantity;
    try {
      await axios.post(
        `${API}/cart?product_id=${id}&quantity=${finalQuantity}`,
        {},
        { withCredentials: true }
      );
      updateCartCount(finalQuantity);
      toast.success(`Added ${finalQuantity} item(s) to cart!`);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to add items to cart');
        const redirectUrl = `${window.location.origin}/auth-callback`;
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
      } else {
        toast.error('Failed to add to cart');
      }
    }
  };

  const handleBuyNow = async () => {
    const finalQuantity = quantity === '' ? 1 : quantity;
    try {
      await axios.post(
        `${API}/cart?product_id=${id}&quantity=${finalQuantity}`,
        {},
        { withCredentials: true }
      );
      updateCartCount(finalQuantity);
      navigate('/cart');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to continue');
        const redirectUrl = `${window.location.origin}/auth-callback`;
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
      } else {
        toast.error('Failed to process request');
      }
    }
  };

  const handleMakeOffer = async () => {
    if (!offerAmount || parseFloat(offerAmount) <= 0) {
      toast.error('Please enter a valid offer amount');
      return;
    }
    try {
      await axios.post(
        `${API}/offers?product_id=${id}&offer_amount=${offerAmount}`,
        {},
        { withCredentials: true }
      );
      toast.success('Offer submitted successfully!');
      setShowOfferModal(false);
      setOfferAmount('');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to make an offer');
      } else {
        toast.error('Failed to submit offer');
      }
    }
  };

  const handleWishlistToggle = async () => {
    setWishlistLoading(true);
    try {
      if (inWishlist) {
        await axios.delete(`${API}/wishlist/remove/${id}`, { withCredentials: true });
        setInWishlist(false);
        updateWishlistCount(-1);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist/add?product_id=${id}`, {}, { withCredentials: true });
        setInWishlist(true);
        updateWishlistCount(1);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to manage wishlist');
        const redirectUrl = `${window.location.origin}/auth-callback`;
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to update wishlist');
      }
    } finally {
      setWishlistLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="loading">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
      </div>
    );
  }

  if (!product) return null;

  const displayMedia = productMedia.length > 0 ? productMedia : [{ 
    id: 'default', 
    file_url: product.image_url, 
    file_type: 'image',
    alt_text: product.name 
  }];

  const currentMedia = displayMedia[selectedMediaIndex];
  const showScrollLeftMobile = thumbnailScrollPosition > 0;
  const showScrollRightMobile = thumbnailScrollPosition < Math.max(0, displayMedia.length - THUMBNAILS_VISIBLE_MOBILE);
  const showScrollUpDesktop = thumbnailScrollPosition > 0;
  const showScrollDownDesktop = thumbnailScrollPosition < Math.max(0, displayMedia.length - THUMBNAILS_VISIBLE_DESKTOP);

  return (
    <div className="min-h-screen bg-black" data-testid="product-detail-page">
      <Navbar />
      
      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* LEFT SIDE - Product Media Gallery */}
            <div className="space-y-4">
              {/* MOBILE: Horizontal thumbnails with arrows ABOVE hero image */}
              {productMedia.length > 1 && (
                <div className="md:hidden">
                  <div className="flex items-center justify-center gap-2">
                    {/* Left Arrow */}
                    <button
                      onClick={scrollThumbnailsLeft}
                      disabled={!showScrollLeftMobile}
                      className={`p-2 rounded-lg transition ${
                        showScrollLeftMobile 
                          ? 'bg-white/10 hover:bg-white/20 text-red-500' 
                          : 'bg-white/5 text-gray-600 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <ChevronLeft size={20} />
                    </button>

                    {/* 4 Thumbnails */}
                    <div className="flex gap-2">
                      {displayMedia
                        .slice(thumbnailScrollPosition, thumbnailScrollPosition + THUMBNAILS_VISIBLE_MOBILE)
                        .map((media, displayIndex) => {
                          const actualIndex = thumbnailScrollPosition + displayIndex;
                          return (
                            <button
                              key={media.id || actualIndex}
                              onClick={() => setSelectedMediaIndex(actualIndex)}
                              className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                                selectedMediaIndex === actualIndex 
                                  ? 'border-red-500 shadow-neon-red' 
                                  : 'border-white/20 opacity-60'
                              }`}
                            >
                              {media.file_type === 'image' ? (
                                <img 
                                  src={media.file_url} 
                                  alt={`Thumbnail ${actualIndex + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                  <Play className="text-white" size={16} fill="white" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                    </div>

                    {/* Right Arrow */}
                    <button
                      onClick={scrollThumbnailsRight}
                      disabled={!showScrollRightMobile}
                      className={`p-2 rounded-lg transition ${
                        showScrollRightMobile 
                          ? 'bg-white/10 hover:bg-white/20 text-red-500' 
                          : 'bg-white/5 text-gray-600 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              )}

              {/* DESKTOP: Vertical thumbnails on LEFT + Hero Image on RIGHT */}
              <div className="hidden md:flex md:gap-4">
                {/* Vertical Thumbnail Strip - Desktop Only */}
                {productMedia.length > 1 && (
                  <div className="flex flex-col items-center gap-2">
                    {/* Scroll Up Button */}
                    <button
                      onClick={scrollThumbnailsUp}
                      disabled={!showScrollUpDesktop}
                      className={`w-20 h-10 flex items-center justify-center rounded-lg transition-all ${
                        showScrollUpDesktop 
                          ? 'bg-white/10 hover:bg-white/20 border border-white/20' 
                          : 'bg-white/5 border border-white/10 opacity-30 cursor-not-allowed'
                      }`}
                    >
                      <ChevronUp size={20} className={showScrollUpDesktop ? 'text-red-500' : 'text-gray-600'} />
                    </button>

                    {/* Thumbnail Container */}
                    <div 
                      ref={thumbnailContainerRef}
                      className="flex flex-col gap-3 overflow-hidden"
                      style={{ height: `${THUMBNAILS_VISIBLE_DESKTOP * THUMBNAIL_HEIGHT}px` }}
                    >
                      {displayMedia
                        .slice(thumbnailScrollPosition, thumbnailScrollPosition + THUMBNAILS_VISIBLE_DESKTOP)
                        .map((media, displayIndex) => {
                          const actualIndex = thumbnailScrollPosition + displayIndex;
                          return (
                            <button
                              key={media.id || actualIndex}
                              onClick={() => setSelectedMediaIndex(actualIndex)}
                              className={`relative w-20 h-20 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                                selectedMediaIndex === actualIndex 
                                  ? 'border-red-500 shadow-neon-red scale-105' 
                                  : 'border-white/20 opacity-60 hover:opacity-100 hover:border-white/40'
                              }`}
                            >
                              {media.file_type === 'image' ? (
                                <img 
                                  src={media.file_url} 
                                  alt={`Thumbnail ${actualIndex + 1}`}
                                  className="w-full h-full object-cover"
                                  loading={actualIndex < 6 ? 'eager' : 'lazy'}
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-800 flex items-center justify-center relative">
                                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                    <Play className="text-white" size={20} fill="white" />
                                  </div>
                                </div>
                              )}
                            </button>
                          );
                        })}
                    </div>

                    {/* Scroll Down Button */}
                    <button
                      onClick={scrollThumbnailsDown}
                      disabled={!showScrollDownDesktop}
                      className={`w-20 h-10 flex items-center justify-center rounded-lg transition-all ${
                        showScrollDownDesktop 
                          ? 'bg-white/10 hover:bg-white/20 border border-white/20' 
                          : 'bg-white/5 border border-white/10 opacity-30 cursor-not-allowed'
                      }`}
                    >
                      <ChevronDown size={20} className={showScrollDownDesktop ? 'text-red-500' : 'text-gray-600'} />
                    </button>
                  </div>
                )}

                {/* Hero Image - Desktop */}
                <div className="flex-1">
                  <div 
                    className="relative rounded-2xl overflow-hidden cursor-pointer group"
                    onClick={() => setLightboxOpen(true)}
                    data-testid="product-image"
                  >
                    {currentMedia.file_type === 'image' ? (
                      <ImageMagnifier 
                        src={currentMedia.file_url} 
                        alt={currentMedia.alt_text || product.name}
                      />
                    ) : (
                      <video 
                        src={currentMedia.file_url} 
                        controls 
                        className="w-full h-auto object-cover"
                      />
                    )}

                    {/* Zoom Icon */}
                    {currentMedia.file_type === 'image' && (
                      <div className="absolute top-4 right-4 bg-black/60 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <ZoomIn size={20} />
                      </div>
                    )}

                    {/* Stock Warning */}
                    {product.stock < 10 && product.stock > 0 && (
                      <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-600 to-orange-600 text-white px-4 py-2 rounded-full font-bold text-sm shadow-neon-orange animate-pulse-subtle">
                        Only {product.stock} left!
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* MOBILE: Hero Image (no thumbnails here) */}
              <div className="md:hidden">
                <div 
                  className="relative rounded-2xl overflow-hidden cursor-pointer group"
                  onClick={() => setLightboxOpen(true)}
                  data-testid="product-image"
                >
                  <img 
                    src={currentMedia.file_url} 
                    alt={currentMedia.alt_text || product.name} 
                    className="w-full h-auto object-cover"
                  />

                  {/* Stock Warning */}
                  {product.stock < 10 && product.stock > 0 && (
                    <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-600 to-orange-600 text-white px-3 py-1.5 rounded-full font-bold text-xs shadow-neon-orange animate-pulse-subtle">
                      Only {product.stock} left!
                    </div>
                  )}
                </div>
              </div>
              
              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4">
                <div className="glass-effect p-4 rounded-lg text-center" data-testid="badge-authentic">
                  <Shield className="mx-auto mb-2 text-red-500" size={24} />
                  <p className="text-xs text-gray-400">100% Authentic</p>
                </div>
                <div className="glass-effect p-4 rounded-lg text-center" data-testid="badge-shipping">
                  <Package className="mx-auto mb-2 text-red-500" size={24} />
                  <p className="text-xs text-gray-400">Fast Shipping</p>
                </div>
                <div className="glass-effect p-4 rounded-lg text-center" data-testid="badge-quality">
                  <TrendingUp className="mx-auto mb-2 text-red-500" size={24} />
                  <p className="text-xs text-gray-400">Premium Quality</p>
                </div>
              </div>
            </div>

            {/* RIGHT SIDE - Product Details */}
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-red-500 font-semibold" data-testid="product-category">{product.category}</span>
                  <span className="text-sm text-gray-400" data-testid="product-type">{product.type}</span>
                </div>
                <h1 className="text-4xl font-bold mb-4" data-testid="product-name">{product.name}</h1>
                
                {/* Updated Description with Show More/Less */}
                <div data-testid="product-description">
                  <p className={`text-gray-400 text-lg leading-relaxed ${!showFullDescription ? 'line-clamp-2' : ''}`}>
                    {product.description}
                  </p>
                  {product.description.length > 150 && (
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      className="text-red-500 hover:text-red-400 text-sm font-semibold mt-2 flex items-center gap-1 transition-colors"
                    >
                      {showFullDescription ? (
                        <>
                          Show Less
                          <ChevronUp size={16} />
                        </>
                      ) : (
                        <>
                          Show More
                          <ChevronDown size={16} />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="border-t border-white/10 pt-6">
                <div className="mb-6">
                  <div className="flex items-center gap-3 flex-wrap">
                    {product.discount_price ? (
                      <>
                        <p className="text-5xl font-bold text-red-500" data-testid="product-discount-price">
                          ₹{product.discount_price.toLocaleString()}
                        </p>
                        <p className="text-3xl font-bold text-gray-500 line-through" data-testid="product-original-price">
                          ₹{product.price.toLocaleString()}
                        </p>
                        <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                          {Math.round((1 - product.discount_price / product.price) * 100)}% OFF
                        </span>
                      </>
                    ) : (
                      <p className="text-5xl font-bold accent-red" data-testid="product-price">
                        ₹{product.price.toLocaleString()}
                      </p>
                    )}
                  </div>
                  
                  {/* Stock indicator */}
                  {product.stock < 10 && product.stock > 0 && (
                    <div className="mt-3 inline-flex items-center gap-2 bg-yellow-600/20 border border-yellow-600/50 px-3 py-1.5 rounded-full">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
                      <span className="text-yellow-500 text-sm font-semibold">
                        Only {product.stock} left in stock
                      </span>
                    </div>
                  )}
                </div>

                {product.stock > 0 ? (
                  <>
                    <div className="mb-6">
                      <label className="block text-sm font-semibold mb-3">Quantity</label>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={handleDecreaseQuantity}
                            className={`bg-white/5 hover:bg-white/10 border border-white/10 w-10 h-10 rounded-lg font-bold flex items-center justify-center transition ${
                              quantity <= 1 ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            data-testid="quantity-decrease"
                            disabled={quantity <= 1}
                          >
                            <Minus size={18} />
                          </button>
                          
                          <input
                            type="number"
                            value={quantity}
                            onChange={handleQuantityChange}
                            onBlur={handleQuantityBlur}
                            className="w-16 h-10 text-center text-xl font-bold bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-red-500"
                            data-testid="quantity-input"
                            min="1"
                            max="100"
                          />
                          
                          <button
                            onClick={handleIncreaseQuantity}
                            className={`bg-white/5 hover:bg-white/10 border border-white/10 w-10 h-10 rounded-lg font-bold flex items-center justify-center transition ${
                              quantity >= 100 ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            data-testid="quantity-increase"
                            disabled={quantity >= 100}
                          >
                            <Plus size={18} />
                          </button>
                        </div>

                        <button
                          onClick={handleAddToCart}
                          className="flex-1 btn-primary flex items-center justify-center space-x-2"
                          data-testid="add-to-cart-btn"
                        >
                          <ShoppingCart size={20} />
                          <span>Add to Cart</span>
                        </button>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <button
                        onClick={handleBuyNow}
                        className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-4 rounded-lg flex items-center justify-center space-x-2 transition"
                        data-testid="buy-now-btn"
                      >
                        <ShoppingCart size={20} />
                        <span>Buy Now</span>
                      </button>

                      <div className="flex space-x-4">
                        <button 
                          onClick={handleWishlistToggle}
                          disabled={wishlistLoading}
                          className={`flex-1 py-3 rounded-lg flex items-center justify-center space-x-2 transition-all ${
                            inWishlist 
                              ? 'bg-red-600 hover:bg-red-700 text-white' 
                              : 'glass-effect hover:bg-white/10 border border-white/10'
                          } ${wishlistLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          data-testid="wishlist-btn"
                        >
                          <Heart size={20} className={inWishlist ? 'fill-white' : ''} />
                          <span>{inWishlist ? 'In Wishlist' : 'Add to Wishlist'}</span>
                        </button>
                        <button 
                          onClick={() => setShowShareModal(true)}
                          className="flex-1 glass-effect hover:bg-white/10 border border-white/10 py-3 rounded-lg flex items-center justify-center space-x-2" 
                          data-testid="share-btn"
                        >
                          <Share2 size={20} />
                          <span>Share</span>
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-red-600/20 border border-red-600 rounded-lg p-6 text-center" data-testid="out-of-stock">
                    <p className="text-xl font-bold text-red-500">OUT OF STOCK</p>
                    <p className="text-gray-400 mt-2">This item is currently unavailable</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <ReviewSection productId={id} product={product} />
        </div>
      </div>

      <ProductLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        slides={lightboxSlides()}
        initialIndex={selectedMediaIndex}
      />

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        product={product}
        url={window.location.href}
      />

      {showOfferModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowOfferModal(false)} data-testid="offer-modal">
          <div className="bg-gray-900 rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-4" data-testid="offer-modal-title">Make an Offer</h2>
            <p className="text-gray-400 mb-6">Submit your best offer for this collectible. We'll review and get back to you.</p>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">Your Offer Amount (₹)</label>
              <input
                type="number"
                value={offerAmount}
                onChange={(e) => setOfferAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                data-testid="offer-amount-input"
              />
              <p className="text-sm text-gray-400 mt-2">Original Price: ₹{product.price.toLocaleString()}</p>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setShowOfferModal(false)}
                className="flex-1 btn-secondary"
                data-testid="offer-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleMakeOffer}
                className="flex-1 btn-primary"
                data-testid="offer-submit-btn"
              >
                Submit Offer
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetail;